import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { skillsAPI, cvAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ChevronLeft, Target, BookOpen, ExternalLink, CheckCircle,
  XCircle, Loader2, Zap, TrendingUp, Award, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';

const SkillsAnalyzerPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [cvs, setCvs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedCV, setSelectedCV] = useState('');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [jobsRes, cvsRes] = await Promise.all([
        skillsAPI.getJobs(),
        cvAPI.list()
      ]);
      setJobs(jobsRes.data.jobs);
      setCvs(cvsRes.data);
      if (cvsRes.data.length > 0) {
        setSelectedCV(cvsRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedJob) {
      toast.error('Please select a target job');
      return;
    }
    if (!isSubscribed) {
      toast.error('Subscribe to use the Skills Analyzer');
      navigate('/subscribe');
      return;
    }

    setAnalyzing(true);
    setResult(null);

    try {
      const response = await skillsAPI.analyze({
        target_job: selectedJob,
        cv_id: selectedCV || null
      });
      setResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const getReadinessColor = (color) => {
    switch (color) {
      case 'green': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'amber': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'red': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-br from-purple-600 via-purple-500 to-indigo-600 text-white px-4 lg:px-8 py-8">
        <div className="max-w-5xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-purple-200 hover:text-white hover:bg-purple-700 mb-4"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold">Skills Gap Analyzer</h1>
              <p className="text-purple-200">Find what skills you need and where to learn them - for free</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-8 px-4 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-purple-500" />
                Analyze Your Skills
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    What job do you want?
                  </label>
                  <Select value={selectedJob} onValueChange={setSelectedJob}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select your target job" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.map((job) => (
                        <SelectItem key={job.id} value={job.id}>
                          <div className="flex items-center justify-between w-full">
                            <span>{job.title}</span>
                            <span className="text-xs text-slate-400 ml-2">{job.salary_range}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Your CV (to extract your skills)
                  </label>
                  {cvs.length > 0 ? (
                    <Select value={selectedCV} onValueChange={setSelectedCV}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select a CV" />
                      </SelectTrigger>
                      <SelectContent>
                        {cvs.map((cv) => (
                          <SelectItem key={cv.id} value={cv.id}>
                            {cv.title || cv.personal_details?.full_name || 'Untitled CV'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-slate-600 text-sm mb-2">No CV found</p>
                      <Button size="sm" onClick={() => navigate('/cv-builder')}>
                        Create CV First
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    We'll compare your CV skills with job requirements
                  </p>
                </div>

                <Button
                  onClick={handleAnalyze}
                  disabled={analyzing || !selectedJob}
                  className="w-full h-12 bg-purple-600 hover:bg-purple-700"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Target className="w-5 h-5 mr-2" />
                      Analyze My Skills Gap
                    </>
                  )}
                </Button>
              </div>

              {/* Job List Preview */}
              <div className="mt-6 pt-6 border-t border-slate-100">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Popular Jobs in SA</h3>
                <div className="space-y-2">
                  {jobs.slice(0, 5).map((job) => (
                    <button
                      key={job.id}
                      onClick={() => setSelectedJob(job.id)}
                      className={`w-full text-left p-3 rounded-lg transition-all text-sm ${
                        selectedJob === job.id
                          ? 'bg-purple-50 border border-purple-200'
                          : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-slate-900">{job.title}</span>
                        <span className="text-xs text-slate-500">{job.salary_range}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div>
              {result ? (
                <div className="space-y-4">
                  {/* Readiness Score */}
                  <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">{result.job.title}</h3>
                      <p className="text-sm text-slate-500">{result.job.salary_range}</p>
                    </div>

                    <div className="flex items-center justify-center gap-4 mb-4">
                      <div className="text-center">
                        <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full text-2xl font-bold ${
                          result.analysis.match_percentage >= 80 ? 'bg-emerald-100 text-emerald-600' :
                          result.analysis.match_percentage >= 50 ? 'bg-amber-100 text-amber-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {result.analysis.match_percentage}%
                        </div>
                        <p className="text-sm text-slate-500 mt-2">Match</p>
                      </div>
                    </div>

                    <div className={`text-center p-3 rounded-xl border ${getReadinessColor(result.analysis.readiness_color)}`}>
                      <Award className="w-5 h-5 inline mr-2" />
                      <span className="font-semibold">{result.analysis.readiness}</span>
                    </div>
                  </div>

                  {/* Skills Breakdown */}
                  <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Skills Breakdown</h3>
                    
                    {/* Matching Skills */}
                    {result.analysis.matching_skills.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-emerald-600 mb-2 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Skills You Have ({result.analysis.matching_skills.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {result.analysis.matching_skills.map((skill, i) => (
                            <span key={i} className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Missing Skills */}
                    {result.analysis.missing_skills.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-red-600 mb-2 flex items-center gap-1">
                          <XCircle className="w-4 h-4" />
                          Skills to Learn ({result.analysis.missing_skills.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {result.analysis.missing_skills.map((skill, i) => (
                            <span key={i} className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Nice to Have */}
                    {result.analysis.nice_to_have.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-slate-500 mb-2">Nice to Have (Bonus)</p>
                        <div className="flex flex-wrap gap-2">
                          {result.analysis.nice_to_have.map((skill, i) => (
                            <span key={i} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tip */}
                  {result.tip && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <p className="text-amber-800 flex items-start gap-2">
                        <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        {result.tip}
                      </p>
                    </div>
                  )}

                  {/* Learning Path */}
                  {result.learning_path && result.learning_path.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-6">
                      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-purple-500" />
                        Free Courses to Close the Gap
                      </h3>
                      
                      <div className="space-y-4">
                        {result.learning_path.map((item, index) => (
                          <div key={index} className="border border-slate-100 rounded-xl p-4">
                            <h4 className="font-medium text-slate-900 mb-2">{item.skill}</h4>
                            <div className="space-y-2">
                              {item.courses.map((course, ci) => (
                                <a
                                  key={ci}
                                  href={course.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-purple-50 transition-colors group"
                                >
                                  <div>
                                    <p className="text-sm font-medium text-slate-900 group-hover:text-purple-600">
                                      {course.name}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {course.provider} • {course.duration}
                                    </p>
                                  </div>
                                  <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-purple-500" />
                                </a>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-slate-100 rounded-2xl p-8 text-center h-full flex flex-col items-center justify-center min-h-[400px]">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <Target className="w-8 h-8 text-purple-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Skills Analysis Results</h3>
                  <p className="text-slate-600 max-w-sm">
                    Select a job and click "Analyze" to see which skills you have, which you need, and where to learn them for free.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SkillsAnalyzerPage;
